import { useState } from "react";
import { Sparkles } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Skeleton } from "@/components/ui/skeleton";


interface PartnerSupplier {
  id: string;
  name: string;
  logo_url: string | null;
}

const LogoCloudSection = () => {
  const [failedImages, setFailedImages] = useState<Set<string>>(new Set());

  const { data: partners, isLoading } = useQuery({
    queryKey: ["partner-suppliers-logos-salgados"],
    queryFn: async () => {
      const { data: suppliers, error } = await (supabase as any)
        .from("suppliers_public")
        .select("id, name, avatar_url, status_onboarding")
        .not("name", "is", null)
        .in("status_onboarding", ["dados_gerenciais", "cardapio", "ativo"])
        .order("name");

      if (error) throw error;
      if (!suppliers) return [];

      return suppliers.map((s: any) => ({
        id: s.id,
        name: s.name,
        logo_url: s.avatar_url || null,
      })) as PartnerSupplier[];
    },
    staleTime: 1000 * 60 * 10,
  });

  const getInitials = (name: string) => {
    return name
      .split(" ")
      .slice(0, 2)
      .map((word) => word[0])
      .join("")
      .toUpperCase();
  };

  const handleImageError = (id: string) => {
    setFailedImages((prev) => new Set(prev).add(id));
  };

  // Filter out test suppliers and AmeUp Doces; keep only Naha Salgados; require logo
  const filteredPartners = partners?.filter((partner) => {
    const name = partner.name.toLowerCase();
    if (!partner.logo_url) return false;
    if (name.includes("ameup doces")) return false;
    if (name.includes("naha") && !name.includes("salgados")) return false;
    if (name.includes("lais neves")) return false;
    if (name.includes("teste")) return false;
    if (name.includes("sandbox")) return false;
    return true;
  }) || [];

  // Repeat enough times to fill the marquee track and avoid white gaps
  const MIN_ITEMS = 16;
  const repeatCount = filteredPartners.length > 0
    ? Math.max(2, Math.ceil(MIN_ITEMS / filteredPartners.length) * 2)
    : 0;
  const displayPartners = Array.from({ length: repeatCount }, () => filteredPartners).flat();

  return (
    <section className="py-16 bg-white overflow-hidden">
      <div className="container mx-auto px-4 mb-8">
        <div className="flex justify-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-brand-purple/10 rounded-full text-brand-purple text-sm font-medium bg-zinc-100 text-slate-500">
            <Sparkles className="w-4 h-4" />
            Confiado por empresas incríveis
          </div>
        </div>
      </div>

      <div className="relative">
        <div className="absolute left-0 top-0 bottom-0 w-32 bg-gradient-to-r from-white to-transparent z-10" />
        <div className="absolute right-0 top-0 bottom-0 w-32 bg-gradient-to-l from-white to-transparent z-10" />

        <div className="logo-cloud-track">
          {isLoading ? (
            Array.from({ length: 8 }).map((_, index) => (
              <div key={index} className="flex-shrink-0 mx-4">
                <div className="flex items-center gap-2 px-3 py-2 rounded-full bg-gray-100">
                  <Skeleton className="w-8 h-8 rounded-full" />
                  <Skeleton className="w-20 h-4 rounded" />
                </div>
              </div>
            ))
          ) : (
            displayPartners.map((partner, index) => (
              <div
                key={`${partner.id}-${index}`}
                className="flex-shrink-0 mx-3 flex items-center gap-2 px-3 py-2 rounded-full bg-gray-100 border border-gray-200 transition-all duration-300 hover:bg-gray-150 hover:shadow-sm cursor-pointer"
              >
                {partner.logo_url && !failedImages.has(partner.id) && (
                  <img
                    src={partner.logo_url}
                    alt={partner.name}
                    className="w-8 h-8 rounded-full object-cover bg-white grayscale opacity-60"
                    onError={() => handleImageError(partner.id)}
                  />
                )}
                <span className="text-sm text-gray-400 font-medium whitespace-nowrap">
                  {partner.name}
                </span>
              </div>
            ))
          )}
        </div>
      </div>
    </section>
  );
};

export default LogoCloudSection;
